import * as Yup from 'yup';
import {IPersonalityType} from '../../../../../../services/hookService/getUserCooperationAgreementData/types';

const defaultErrorYear = '1402/01/02';
const persianDateRegex = /^\d{4}\/\d{1,2}\/\d{1,2}$/;
const onlyDigitsRegex = /^\d+$/;
const exactly10DigitsRegex = /^\d{10}$/;
const phoneNumberRegex = /^09[0-9]{9}$/;
const persianNameRegex = /^[\u0600-\u06FF\uFB8A\u067E\u0686\u0698\u200C\s]+$/;

// Check if ANY second representative field has a value
const hasAnySecondRepValue = (parent: any): boolean => {
  if (!parent) return false;

  const name = parent.second_legal_representative_name;
  const code = parent.second_legal_representative_code;
  const phone = parent.second_legal_representative_phone_number;

  return (
    (name && String(name).trim() !== '') ||
    (code && String(code).trim() !== '') ||
    (phone && String(phone).trim() !== '')
  );
};

export const getValidationSchema = (userType: IPersonalityType) => {
  const commonLicenseInfo = {
    license_number: Yup.string()
      .required('شماره مجوز الزامی است')
      .matches(onlyDigitsRegex, 'شماره مجوز فقط باید شامل عدد باشد')
      .min(5, 'شماره مجوز باید حداقل ۵ رقم باشد')
      .max(20, 'شماره مجوز باید حداکثر ۲۰ رقم باشد'),

    license_issuance_date: Yup.string()
      .required('تاریخ صدور مجوز الزامی است')
      .matches(
        persianDateRegex,
        `فرمت تاریخ باید به صورت ${defaultErrorYear} باشد`,
      ),
  };

  const commonStoreFields = {
    phone_number: Yup.string()
      .required('شماره تماس فروشگاه الزامی است')
      .matches(
        phoneNumberRegex,
        'شماره تماس باید با 09 شروع شود و ۱۱ رقم باشد',
      ),

    address: Yup.string()
      .required('آدرس فروشگاه الزامی است')
      .min(10, 'آدرس باید حداقل ۱۰ حرف باشد')
      .max(500, 'آدرس باید حداکثر ۵۰۰ حرف باشد'),

    title: Yup.string()
      .required('اسم فروشگاه الزامی است')
      .min(2, 'اسم فروشگاه باید حداقل ۳ حرف باشد')
      .max(100, 'اسم فروشگاه باید حداکثر ۱۰۰ حرف باشد')
      .matches(persianNameRegex, 'اسم فروشگاه باید فقط شامل حروف فارسی باشد'),

    postal_code: Yup.string()
      .required('کد پستی فروشگاه الزامی است')
      .matches(exactly10DigitsRegex, 'کد پستی باید دقیقاً ۱۰ رقم باشد'),
  };

  const corporateLicenseInfo = {
    company_name: Yup.string()
      .required('نام شخصیت حقوقی الزامی است')
      .min(2, 'نام شخصیت حقوقی باید حداقل ۳ حرف باشد')
      .max(100, 'نام شخصیت حقوقی باید حداکثر ۱۰۰ حرف باشد')
      .matches(
        persianNameRegex,
        'نام شخصیت حقوقی باید فقط شامل حروف فارسی باشد',
      ),

    national_company_code: Yup.string()
      .required('شناسه ملی الزامی است')
      .matches(onlyDigitsRegex, 'شناسه ملی فقط باید شامل عدد باشد')
      .length(11, 'شناسه ملی باید دقیقاً 11 رقم باشد'),

    registration_number: Yup.string()
      .required('شماره ثبت الزامی است')
      .matches(onlyDigitsRegex, 'شماره ثبت فقط باید شامل عدد باشد')
      .min(5, 'شماره ثبت باید حداقل ۵ رقم باشد')
      .max(20, 'شماره ثبت باید حداکثر ۲۰ رقم باشد'),

    first_legal_representative_name: Yup.string()
      .required('نام نماینده قانونی اول الزامی است')
      .min(3, 'نام نماینده باید حداقل ۳ حرف باشد')
      .max(50, 'نام نماینده باید حداکثر ۵۰ حرف باشد')
      .matches(persianNameRegex, 'نام نماینده باید فقط شامل حروف فارسی باشد'),

    first_legal_representative_code: Yup.string()
      .required('کد ملی نماینده اول الزامی است')
      .matches(onlyDigitsRegex, 'کد ملی فقط باید شامل عدد باشد')
      .length(10, 'کد ملی باید دقیقاً ۱۰ رقم باشد'),

    first_legal_representative_phone_number: Yup.string()
      .required('موبایل نماینده اول الزامی است')
      .matches(
        phoneNumberRegex,
        'شماره تماس باید با 09 شروع شود و ۱۱ رقم باشد',
      ),

    // Second representative fields - all 3 required if any 1 has value
    second_legal_representative_name: Yup.string()
      .nullable()
      .test(
        'second-rep-name-required',
        'نام نماینده قانونی دوم الزامی است',
        function (value) {
          if (hasAnySecondRepValue(this.parent)) {
            if (!value || String(value).trim() === '') {
              return this.createError({
                message: 'نام نماینده قانونی دوم الزامی است',
              });
            }
            if (String(value).trim().length < 3) {
              return this.createError({
                message: 'نام نماینده باید حداقل ۳ حرف باشد',
              });
            }
            if (String(value).trim().length > 50) {
              return this.createError({
                message: 'نام نماینده باید حداکثر ۵۰ حرف باشد',
              });
            }
            if (!persianNameRegex.test(String(value))) {
              return this.createError({
                message: 'نام نماینده باید فقط شامل حروف فارسی باشد',
              });
            }
          }
          return true;
        },
      ),

    second_legal_representative_code: Yup.string()
      .nullable()
      .test(
        'second-rep-code-required',
        'کد ملی نماینده دوم الزامی است',
        function (value) {
          if (hasAnySecondRepValue(this.parent)) {
            if (!value || String(value).trim() === '') {
              return this.createError({
                message: 'کد ملی نماینده دوم الزامی است',
              });
            }
            if (!onlyDigitsRegex.test(String(value))) {
              return this.createError({
                message: 'کد ملی فقط باید شامل عدد باشد',
              });
            }
            if (String(value).length !== 10) {
              return this.createError({
                message: 'کد ملی باید دقیقاً ۱۰ رقم باشد',
              });
            }
          }
          return true;
        },
      ),

    second_legal_representative_phone_number: Yup.string()
      .nullable()
      .test(
        'second-rep-phone-required',
        'موبایل نماینده دوم الزامی است',
        function (value) {
          if (hasAnySecondRepValue(this.parent)) {
            if (!value || String(value).trim() === '') {
              return this.createError({
                message: 'موبایل نماینده دوم الزامی است',
              });
            }
            if (!phoneNumberRegex.test(String(value))) {
              return this.createError({
                message: 'شماره تماس باید با 09 شروع شود و ۱۱ رقم باشد',
              });
            }
          }
          return true;
        },
      ),
  };

  if (userType === IPersonalityType.Individual) {
    return Yup.object({
      name: Yup.string()
        .required('نام مالک فروشگاه الزامی است')
        .min(3, 'نام مالک باید حداقل ۳ حرف باشد')
        .max(50, 'نام مالک باید حداکثر ۵۰ حرف باشد'),

      national_code: Yup.string()
        .required('کد ملی مالک فروشگاه الزامی است')
        .matches(onlyDigitsRegex, 'کد ملی فقط باید شامل عدد باشد')
        .length(10, 'کد ملی باید دقیقاً ۱۰ رقم باشد'),

      store: Yup.object({
        ...commonStoreFields,
        license_info: Yup.object(commonLicenseInfo),
      }),
    });
  }

  return Yup.object({
    name: Yup.string()
      .required('نام شخصیت حقوقی الزامی است')
      .min(3, 'نام شخصیت حقوقی باید حداقل ۳ حرف باشد')
      .max(100, 'نام شخصیت حقوقی باید حداکثر ۱۰۰ حرف باشد'),

    store: Yup.object({
      ...commonStoreFields,
      license_info: Yup.object({
        ...commonLicenseInfo,
        ...corporateLicenseInfo,
      }),
    }),
  });
};
